import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ModuleWidgetService } from '../../../modules/shared/services/dynamic-table/module-widget.service';
import { Subject, takeUntil } from 'rxjs';
import { DynamicTableChildWidgetM, DynamicTableModuleWidgetM } from '../../../modules/shared/models/dynamic-table/dynamic-table-module-widget.model';

@Component({
  selector: 'app-custom-wb-module',
  templateUrl: './custom-wb-module.component.html',
  styleUrl: './custom-wb-module.component.scss',
})
export class CustomWbModuleComponent implements OnInit, OnDestroy {
  // Submodule related properties
  subModuleWidgets: DynamicTableChildWidgetM[] = [];
  selectedSubModule: DynamicTableChildWidgetM | null = null;
  showSubModuleWidgets: boolean = false;
  isShowSideBtn: boolean = false;

  // Main module information
  mainModule: DynamicTableModuleWidgetM | null = null;

  // Navigation state tracking
  activeNavigation = {
    module: true,  // true = module is active, false = submodule is active
    subModuleId: null as number | null
  };

  // Module identification - this is the custom module ID from route
  customModuleWBId: number | null = null;

  // Flag to track if initial data is loaded
  private isInitialDataLoaded: boolean = false;

  // Lifecycle management
  private ngUnsubscribe = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private workbookService: ModuleWidgetService
  ) {
    // Initial load when route params change
    this.route.params.subscribe(params => {
      this.customModuleWBId = params['workbook'];
      // Only initialize if we have the module ID
      if (this.customModuleWBId) {
        this.loadSubModuleWidgets();
        this.findMainModule();
      }
    });
  }

  ngOnInit(): void {
    // Subscribe to module widget changes to auto-update when widgets change
    this.workbookService.getModuleWidgets()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((moduleWidgets: DynamicTableModuleWidgetM[]) => {
          if (moduleWidgets && moduleWidgets.length > 0 && this.customModuleWBId) {
          this.isInitialDataLoaded = true;
          this.findMainModule();
          this.loadSubModuleWidgets();
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  findMainModule(): void {
    if (this.customModuleWBId && this.isInitialDataLoaded) {
      this.mainModule = this.workbookService.getModuleWidgetById(this.customModuleWBId) || null;
      
      // If custom module is deleted, redirect to workspace
      if (!this.mainModule) {
        this.router.navigate(['/workspace']);
      }
    }
  }

  loadSubModuleWidgets(): void {
    if (this.customModuleWBId) {
      const newSubModules = this.workbookService.getCustomSubModules(this.customModuleWBId) || [];
      
      // If user is on a submodule and it gets deleted, redirect to main module
      if (this.selectedSubModule && 
          !newSubModules.find(m => m.id === this.selectedSubModule?.id)) {
        this.navigateToMainModule();
      }
      
      this.subModuleWidgets = newSubModules;
      
      if (this.subModuleWidgets.length > 0) {
        this.isShowSideBtn = true;
      }
      
      this.selectedSubModule = null;
      this.showSubModuleWidgets = false;
      this.activeNavigation.module = true;
      this.activeNavigation.subModuleId = null;
    }
  }

  navigateToSubModule(subModuleWidget: DynamicTableChildWidgetM): void {
    this.selectedSubModule = subModuleWidget;
    this.showSubModuleWidgets = true;
    this.activeNavigation.module = false;
    this.activeNavigation.subModuleId = subModuleWidget.id;
  }

  navigateToMainModule(): void {
    this.selectedSubModule = null;
    this.showSubModuleWidgets = false;
    this.activeNavigation.module = true;
    this.activeNavigation.subModuleId = null;
  }

  changeSideHeader(): void {
    this.isShowSideBtn = !this.isShowSideBtn;
  }
}
